import { Outlet, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import {
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarInset,
  SidebarProvider,
  SidebarRail,
} from "@/components/ui/sidebar";
import { useAuthState } from "@/modules/auth/presentation/model/useAuthState";
import { RequireTwoFactorSetup } from "@/modules/auth/presentation/ui/RequireTwoFactorSetup";
import { BackupReminderBanner } from "@/modules/backups/presentation/ui/BackupReminderBanner";
import { useAdminNotificationStream } from "@/modules/notifications/presentation/hooks/use-admin-notification-stream";
import { useSidebarWithNotifications } from "@/modules/notifications/presentation/hooks/use-sidebar-with-notifications";
import { getCookie } from "@/shared/lib/cookies";
import { cn } from "@/shared/lib/utils";
import { Loader } from "@/shared/ui/Loader";
import { SkipToMain } from "@/shared/ui/SkipToMain";
import { LayoutProvider } from "../providers/LayoutProvider";
import { AppSidebar } from "./AppSidebar";
import { NavGroup } from "./NavGroup";
import { NavUser } from "./NavUser";
import { SidebarBrand } from "./SidebarBrand";

type AuthenticatedLayoutProps = {
  children?: React.ReactNode;
};

export function AuthenticatedLayout({ children }: AuthenticatedLayoutProps) {
  const defaultOpen = getCookie("sidebar_state") !== "false";
  const { isLoading, isAuthenticated } = useAuthState();
  const navigate = useNavigate({ from: "/app" });
  const sidebarData = useSidebarWithNotifications();
  useAdminNotificationStream(isAuthenticated);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate({ to: "/auth/login" });
    }
  }, [isLoading, isAuthenticated, navigate]);

  if (isLoading) {
    return (
      <div className="container grid h-svh max-w-none items-center justify-center">
        <Loader />
      </div>
    );
  }
  return (
    <SidebarProvider defaultOpen={defaultOpen}>
      <LayoutProvider>
        <SkipToMain />
        <AppSidebar>
          <SidebarHeader>
            <SidebarBrand />
          </SidebarHeader>
          <SidebarContent>
            {sidebarData.navGroups.map((props) => (
              <NavGroup key={props.title} {...props} />
            ))}
          </SidebarContent>
          <SidebarFooter>
            <NavUser />
          </SidebarFooter>
          <SidebarRail />
        </AppSidebar>
        <SidebarInset
          className={cn(
            // If layout is fixed, set the height
            // to 100svh to prevent overflow
            "has-[[data-layout=fixed]]:h-svh",

            // If layout is fixed and sidebar is inset,
            // set the height to 100svh - 1rem (total margins) to prevent overflow
            // 'peer-data-[variant=inset]:has-[[data-layout=fixed]]:h-[calc(100svh-1rem)]',
            "peer-data-[variant=inset]:has-[[data-layout=fixed]]:h-[calc(100svh-(var(--spacing)*4))]",

            // Set content container, so we can use container queries
            "@container/content",
          )}
        >
          <BackupReminderBanner />
          <RequireTwoFactorSetup>
            {children ?? <Outlet />}
          </RequireTwoFactorSetup>
        </SidebarInset>
      </LayoutProvider>
    </SidebarProvider>
  );
}
